import { RemoteData } from '@passionware/monads';
import AuthService from 'lib/auth/AuthService';
import Env from 'lib/env/Env';
import { useSelector } from 'react-redux';
import { selectActiveAccount } from 'store/accounts';
import {
  getPublicCompanyName,
  selectBrandImage
} from 'store/v1/auth/auth.selectors';
import type { EnterpriseWorkspace } from 'v5/api/enterprise/enterprise.api';
import { appRouting } from 'v5/core/routing/appRouting';
import { EnterpriseWorkspaceIcon } from 'v5/features/core/enterprise/EnterpriseWorkspaceIcon';
import { EnterpriseWorkspaceSwitcher } from 'v5/features/core/enterprise/EnterpriseWorkspaceSwitcher';
import { SandboxInfoPopover } from 'v5/features/core/sandbox-info/SandboxInfoPopover';
import { DockableSidebarToggle } from 'v5/patterns/sidebar/DockableLayout';
import { SidebarCollapse } from 'v5/patterns/sidebar/Sidebar';
import { useCreateEnterpriseWorkspace } from './useCreateEnterpriseWorkspace';
import { WithServices } from 'v5/platform/ts/services';
import { WithUserFlowService } from 'v5/services/front/UserFlowService/UserFlowService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithNavigationService } from 'v5/services/internal/NavigationService/NavigationService';

export type EnterpriseSidebarIdentityProps = WithServices<
  [WithAppScopeService, WithUserFlowService, WithNavigationService<{}>]
> & {
  organizationId: string;
  workspacesRd: RemoteData<EnterpriseWorkspace[]>;
  /** Absent without an active workspace: the organization stands in for it. */
  activeWorkspace?: EnterpriseWorkspace;
  canManage: boolean;
};

/**
 * Top of the enterprise sidebars: the workspace (or organization) icon, the
 * sandbox badge, the collapse toggle and the workspace switcher.
 */
export function EnterpriseSidebarIdentity({
  services,
  organizationId,
  workspacesRd,
  activeWorkspace,
  canManage
}: EnterpriseSidebarIdentityProps) {
  const companyName = useSelector(getPublicCompanyName) ?? 'Organization';
  const brandImage = useSelector(selectBrandImage) ?? '';
  const account = useSelector(selectActiveAccount);
  const orgRouting = appRouting.org(organizationId);
  const handleCreateWorkspace = useCreateEnterpriseWorkspace(
    { services },
    organizationId
  );

  return (
    <>
      <div className="flex items-center justify-between ml-3">
        <div className="flex items-center gap-2 flex-1">
          <EnterpriseWorkspaceIcon
            name={activeWorkspace?.name ?? companyName}
            iconUrl={activeWorkspace?.iconUrl ?? brandImage}
            className="w-8 h-8 rounded dark:border dark:border-white/20"
          />
          {(!Env.isProd || Env.isQAEnvironment) && <SandboxInfoPopover />}
        </div>
        <DockableSidebarToggle>
          <SidebarCollapse className="group-hocus/sidebar:opacity-100 opacity-0 transition-opacity" />
        </DockableSidebarToggle>
      </div>

      <EnterpriseWorkspaceSwitcher
        workspacesRd={workspacesRd}
        activeWorkspace={
          activeWorkspace && {
            slug: activeWorkspace.slug,
            name: activeWorkspace.name,
            iconUrl: activeWorkspace.iconUrl,
            settingsTo: orgRouting.settings
              .workspace(activeWorkspace.slug)
              .general()
          }
        }
        organizationName={companyName}
        organizationIconUrl={brandImage}
        accountDisplayName={account?.displayName}
        accountProfileImageUrl={account?.profileImage}
        getWorkspacePath={workspaceSlug =>
          orgRouting.workspace(workspaceSlug).dashboard()
        }
        organizationSettingsTo={orgRouting.settings.global.manageEnterprise()}
        accountDetailsTo={orgRouting.settings.personal.account()}
        onLogout={() => AuthService.logout()}
        canManage={canManage}
        onCreateWorkspace={handleCreateWorkspace}
        enterpriseManagementTo={orgRouting.settings.global.manageEnterprise()}
      />
    </>
  );
}
